import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getOrdersInRange, OrderResponse } from "../../../api/order.api";
import { buildCsv, downloadCsv } from "../../../utils/csv";

type Period = "today" | "yesterday" | "last7" | "last30" | "week" | "month" | "year" | "custom";
type Bucket = "hour" | "day" | "month";

const PERIODS: { id: Period; label: string }[] = [
  { id: "today",     label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7",     label: "Last 7 days" },
  { id: "last30",    label: "Last 30 days" },
  { id: "week",      label: "This week" },
  { id: "month",     label: "This month" },
  { id: "year",      label: "This year" },
  { id: "custom",    label: "Custom" },
];

// ---------- Stats-page palette (local; doesn't touch the global monochrome theme) ----------
const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  cream:    "#FEF3C7",
  emerald:  "#059669",
  rose:     "#E11D48",
  amber:    "#F59E0B",
  sky:      "#0EA5E9",
  violet:   "#7C3AED",
  pink:     "#EC4899",
  slate:    "#475569",
};
const HOT_GRAD    = "linear-gradient(135deg,#FB923C 0%,#EA580C 100%)";
const COLD_GRAD   = "linear-gradient(135deg,#38BDF8 0%,#0284C7 100%)";
const FRAPPE_GRAD = "linear-gradient(135deg,#A78BFA 0%,#6D28D9 100%)";
const HERO_GRAD   = "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 45%,#FECACA 100%)";

const CATEGORY_PALETTE = [C.caramel, C.emerald, C.sky, C.violet, C.pink, C.amber, C.rose, C.slate];

// Stable hue from a string → vibrant avatar background
function colorFromName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 70% 55%)`;
}

function bucketForSpan(start: moment.Moment, end: moment.Moment): Bucket {
  const days = end.diff(start, "days");
  if (days <= 1)  return "hour";
  if (days <= 62) return "day";
  return "month";
}

function rangeFor(p: Period, from: string, to: string): { start: string; end: string; bucketBy: Bucket } {
  const now = moment();
  if (p === "today") return { start: now.clone().startOf("day").toISOString(),   end: now.clone().endOf("day").toISOString(),   bucketBy: "hour" };
  if (p === "yesterday") {
    const y = now.clone().subtract(1, "day");
    return { start: y.clone().startOf("day").toISOString(), end: y.clone().endOf("day").toISOString(), bucketBy: "hour" };
  }
  if (p === "last7")  return { start: now.clone().subtract(6,  "days").startOf("day").toISOString(), end: now.clone().endOf("day").toISOString(), bucketBy: "day" };
  if (p === "last30") return { start: now.clone().subtract(29, "days").startOf("day").toISOString(), end: now.clone().endOf("day").toISOString(), bucketBy: "day" };
  if (p === "week")   return { start: now.clone().startOf("week").toISOString(),  end: now.clone().endOf("week").toISOString(),  bucketBy: "day"   };
  if (p === "month")  return { start: now.clone().startOf("month").toISOString(), end: now.clone().endOf("month").toISOString(), bucketBy: "day"   };
  if (p === "year")   return { start: now.clone().startOf("year").toISOString(),  end: now.clone().endOf("year").toISOString(),  bucketBy: "month" };
  const s = moment(from).startOf("day");
  const e = moment(to).endOf("day");
  return { start: s.toISOString(), end: e.toISOString(), bucketBy: bucketForSpan(s, e) };
}

function fmtDuration(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}:${String(s % 60).padStart(2, "0")}`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function exportCsv(orders: OrderResponse[], filename: string): void {
  const headers = [
    "Order ID", "Date", "Time", "Status", "Menu", "Category", "Channel",
    "Type", "Sweetness (%)", "Customer", "Cancelled by",
    "Brewed date", "Brewed time", "Ready date", "Ready time",
    "Picked date", "Picked time", "Remark",
  ];
  const rows = orders.map((o) => {
    const d = moment(o.orderDate);
    // @ts-ignore
    const brewedAt = o.brewedAt ? moment(o.brewedAt) : null;
    // @ts-ignore
    const readyAt = o.readyAt ? moment(o.readyAt) : null;
    // @ts-ignore
    const pickedAt = o.pickedAt ? moment(o.pickedAt) : null;
    // @ts-ignore
    const cancelledByName = o.cancelledBy?.name ?? "";
    return [
      o.id,
      d.format("YYYY-MM-DD"),
      d.format("HH:mm:ss"),
      o.status,
      o.menu?.name ?? "",
      o.menu?.category?.name ?? "",
      o.menu?.category?.categoryFor ?? "",
      o.type ?? "",
      o.sweetness ?? "",
      o.user?.name ?? "",
      cancelledByName,
      brewedAt ? brewedAt.format("YYYY-MM-DD") : "",
      brewedAt ? brewedAt.format("HH:mm:ss") : "",
      readyAt  ? readyAt.format("YYYY-MM-DD") : "",
      readyAt  ? readyAt.format("HH:mm:ss") : "",
      pickedAt ? pickedAt.format("YYYY-MM-DD") : "",
      pickedAt ? pickedAt.format("HH:mm:ss") : "",
      o.remark && o.remark !== "-" ? o.remark : "",
    ];
  });
  downloadCsv(filename, buildCsv(headers, rows));
}

type Channel = "cafe" | "dessert";

const StatsPage: React.FC = () => {
  const [period, setPeriod] = useState<Period>("month");
  const [channel, setChannel] = useState<Channel>("cafe");
  const [from, setFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [to, setTo]     = useState(moment().format("YYYY-MM-DD"));
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const { start, end, bucketBy } = useMemo(() => rangeFor(period, from, to), [period, from, to]);

  // Keep the visible date inputs in sync with the active preset so the user
  // can see exactly which days are being queried — and so editing a date
  // continues from the right starting point.
  useEffect(() => {
    if (period === "custom") return;
    const r = rangeFor(period, from, to);
    const f = moment(r.start).format("YYYY-MM-DD");
    const t = moment(r.end).format("YYYY-MM-DD");
    if (f !== from) setFrom(f);
    if (t !== to) setTo(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    setLoading(true);
    getOrdersInRange(start, end, 5000)
      .then((data) => setOrders(data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [start, end]);

  // Counts per channel — used by the toggle badges (independent of `channel`).
  const channelCounts = useMemo(() => ({
    cafe:    orders.filter((o) => o.menu?.category?.categoryFor === "cafe").length,
    dessert: orders.filter((o) => o.menu?.category?.categoryFor === "dessert").length,
  }), [orders]);

  // The rest of the page reads `cafeOrders` for historical reasons; we just
  // swap the filter to honour the channel toggle.
  const cafeOrders = useMemo(
    () => orders.filter((o) => o.menu?.category?.categoryFor === channel),
    [orders, channel],
  );

  const isDessert = channel === "dessert";

  // ---------- KPIs ----------
  const k = useMemo(() => {
    const total = cafeOrders.length;
    const counts = { ordered: 0, inProgress: 0, done: 0, picked: 0, cancel: 0, rejected: 0 };
    let brewMs = 0, brewN = 0;
    let waitMs = 0, waitN = 0;
    for (const o of cafeOrders) {
      // @ts-ignore
      counts[o.status === "in-progress" ? "inProgress" : (o.status as keyof typeof counts)]++;
      // @ts-ignore
      const b = o.brewedAt; // @ts-ignore
      const r = o.readyAt;  // @ts-ignore
      const p = o.pickedAt;
      if (b && r) { brewMs += new Date(r).getTime() - new Date(b).getTime(); brewN++; }
      if (r && p) { waitMs += new Date(p).getTime() - new Date(r).getTime(); waitN++; }
    }
    const completed = counts.done + counts.picked;
    const cancelled = counts.cancel + counts.rejected;
    return {
      total, counts, completed, cancelled,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      cancelRate:     total > 0 ? Math.round((cancelled / total) * 100) : 0,
      avgBrewMs:      brewN > 0 ? brewMs / brewN : 0,
      avgWaitMs:      waitN > 0 ? waitMs / waitN : 0,
    };
  }, [cafeOrders]);

  // Avg per day across the selected range (counted by calendar day with at least 1 order)
  const avgPerDay = useMemo(() => {
    if (cafeOrders.length === 0) return 0;
    const days = new Set(cafeOrders.map((o) => moment(o.orderDate).format("YYYY-MM-DD")));
    return Math.round((cafeOrders.length / Math.max(days.size, 1)) * 10) / 10;
  }, [cafeOrders]);

  // ---------- Trend buckets ----------
  const trend = useMemo(() => {
    const map = new Map<string, number>();
    if (bucketBy === "hour") {
      for (let h = 7; h <= 18; h++) map.set(String(h), 0);
    } else if (bucketBy === "day") {
      const cur = moment(start);
      const stop = moment(end);
      while (cur.isSameOrBefore(stop, "day")) {
        map.set(cur.format("YYYY-MM-DD"), 0);
        cur.add(1, "day");
      }
    } else {
      const cur = moment(start).startOf("month");
      const stop = moment(end).endOf("month");
      while (cur.isSameOrBefore(stop, "month")) {
        map.set(cur.format("YYYY-MM"), 0);
        cur.add(1, "month");
      }
    }
    for (const o of cafeOrders) {
      const m = moment(o.orderDate);
      const key =
        bucketBy === "hour" ? String(m.hour()) :
        bucketBy === "day"  ? m.format("YYYY-MM-DD") :
        m.format("YYYY-MM");
      if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([key, count]) => ({ key, count }));
  }, [cafeOrders, bucketBy, start, end]);
  const trendMax = Math.max(1, ...trend.map((t) => t.count));

  // ---------- Top menus / categories ----------
  const topMenus = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of cafeOrders) map.set(o.menu.name, (map.get(o.menu.name) ?? 0) + 1);
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [cafeOrders]);
  const topMenuMax = topMenus[0]?.count ?? 1;

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of cafeOrders) {
      const n = o.menu?.category?.name ?? "Unknown";
      map.set(n, (map.get(n) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [cafeOrders]);
  const categoryMax = categories[0]?.count ?? 1;

  // ---------- Type breakdown (hot/cold/frappe) ----------
  const types = useMemo(() => {
    const map: Record<string, number> = { hot: 0, cold: 0, frappe: 0 };
    for (const o of cafeOrders) if (o.type in map) map[o.type]++;
    return [
      { id: "hot",    label: "Hot",    emoji: "🔥", count: map.hot,    grad: HOT_GRAD    },
      { id: "cold",   label: "Cold",   emoji: "🧊", count: map.cold,   grad: COLD_GRAD   },
      { id: "frappe", label: "Frappe", emoji: "🥤", count: map.frappe, grad: FRAPPE_GRAD },
    ];
  }, [cafeOrders]);
  const typeMax = Math.max(1, ...types.map((t) => t.count));

  // ---------- Sweetness ----------
  const sweetness = useMemo(() => {
    const buckets = [0, 25, 50, 75, 100];
    const map = new Map<number, number>(buckets.map((b) => [b, 0]));
    for (const o of cafeOrders) {
      const v = Number(o.sweetness ?? 0);
      const k = buckets.reduce((p, c) => Math.abs(c - v) < Math.abs(p - v) ? c : p, 0);
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([k, v]) => ({ key: `${k}%`, count: v, pct: k }));
  }, [cafeOrders]);
  const sweetMax = Math.max(1, ...sweetness.map((s) => s.count));

  // ---------- Day of week ----------
  const dow = useMemo(() => {
    const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const counts = [0, 0, 0, 0, 0, 0, 0];
    for (const o of cafeOrders) counts[(moment(o.orderDate).isoWeekday() - 1) % 7]++;
    return labels.map((l, i) => ({ key: l, count: counts[i] }));
  }, [cafeOrders]);
  const dowMax = Math.max(1, ...dow.map((d) => d.count));
  const dowMaxIdx = dow.findIndex((d) => d.count === dowMax);

  // ---------- Customers ----------
  const customers = useMemo(() => {
    const map = new Map<number, { id: number; name: string; count: number }>();
    for (const o of cafeOrders) {
      const u = o.user;
      if (!u?.id) continue;
      if (!map.has(u.id)) map.set(u.id, { id: u.id, name: u.name ?? "Unknown", count: 0 });
      map.get(u.id)!.count++;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [cafeOrders]);
  const topCustomers = customers.slice(0, 10);
  const topCustomerMax = topCustomers[0]?.count ?? 1;

  const uniqueCustomers = customers.length;
  const avgPerCustomer = uniqueCustomers > 0
    ? Math.round((cafeOrders.length / uniqueCustomers) * 10) / 10
    : 0;

  // ---------- Cancellation breakdown ----------
  const cancelBreakdown = useMemo(() => {
    let byUser = 0, byBarista = 0;
    for (const o of cafeOrders) {
      if (o.status === "cancel") byUser++;
      else if (o.status === "rejected") byBarista++;
    }
    return { byUser, byBarista };
  }, [cafeOrders]);

  // ---------- Cancellation analytics ----------
  const cancelledOrders = useMemo(
    () => cafeOrders.filter((o) => o.status === "cancel" || o.status === "rejected"),
    [cafeOrders],
  );

  const cancelledTopMenus = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of cancelledOrders) map.set(o.menu.name, (map.get(o.menu.name) ?? 0) + 1);
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [cancelledOrders]);
  const cancelledTopMenuMax = cancelledTopMenus[0]?.count ?? 1;

  const rejectNotes = useMemo(() => {
    return cancelledOrders
      .filter((o) => o.status === "rejected" && o.rejectNote && String(o.rejectNote).trim() !== "" && o.rejectNote !== "-")
      .map((o) => ({
        id: o.id,
        menu: o.menu.name,
        // @ts-ignore
        note: String(o.rejectNote).trim(),
        // @ts-ignore
        when: o.cancelledAt ?? o.updatedAt ?? o.orderDate,
        // @ts-ignore
        by: o.cancelledBy?.name ?? "—",
      }))
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
      .slice(0, 10);
  }, [cancelledOrders]);

  // Group cancellations by reason / category for a quick "why" view.
  const cancelReasons = useMemo(() => {
    const byStatus: Record<string, number> = { cancel: 0, rejected: 0 };
    for (const o of cancelledOrders) {
      if (o.status === "cancel" || o.status === "rejected") byStatus[o.status]++;
    }
    const total = byStatus.cancel + byStatus.rejected || 1;
    return [
      { id: "cancel",   label: "User cancelled", count: byStatus.cancel,   pct: Math.round((byStatus.cancel / total) * 100),   color: C.amber },
      { id: "rejected", label: "Barista rejected", count: byStatus.rejected, pct: Math.round((byStatus.rejected / total) * 100), color: C.rose },
    ];
  }, [cancelledOrders]);

  const periodLabel = period === "custom"
    ? `${moment(start).format("D MMM YYYY")} → ${moment(end).format("D MMM YYYY")}`
    : PERIODS.find((p) => p.id === period)?.label ?? "";

  // Trend chart — find peak so we can emphasise it
  const trendMaxKey = trend.find((t) => t.count === trendMax && trendMax > 0)?.key;

  return (
    <div className="min-h-screen font-sans pb-[40px]" style={{ background: "#FAFAF7" }}>
      {/* ---------- Hero ---------- */}
      <header className="px-5 pt-[24px] pb-[20px]">
        <div
          className="rounded-3xl p-5 md:p-7 relative overflow-hidden"
          style={{ background: HERO_GRAD, boxShadow: "0 12px 36px -16px rgba(217,119,6,0.25)" }}
        >
          {/* Decorative coffee circle */}
          <div
            aria-hidden
            className="absolute -right-12 -top-12 w-[180px] h-[180px] rounded-full opacity-25"
            style={{ background: "radial-gradient(circle,#92400E 0%,transparent 70%)" }}
          />
          <div className="relative flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: C.espresso }}>
                {isDessert ? "🍰 Dessert" : "☕ Cafe"} analytics · {periodLabel}
              </p>
              <h1
                className="font-display font-black mt-2 leading-[0.95] tracking-tightest"
                style={{ color: "#1B0F08", fontSize: "clamp(40px,9vw,68px)" }}
              >
                {loading ? "…" : k.total.toLocaleString()}
              </h1>
              <p className="font-semibold mt-1" style={{ color: C.espresso, fontSize: "15px" }}>
                {isDessert ? "desserts served" : "cups of coffee served"}
              </p>

              {/* Completion ring + small KPI inline */}
              <div className="mt-5 flex items-center gap-5 flex-wrap">
                <Ring percent={k.completionRate} color={C.emerald} label="completed" />
                <div className="flex gap-3">
                  <MiniStat tint={C.emerald} value={k.completed} label="done" />
                  <MiniStat tint={C.rose}    value={k.cancelled} label="cancelled" />
                  <MiniStat tint={C.violet}  value={avgPerDay}   label="avg/day" mono />
                </div>
              </div>
            </div>

            <button
              type="button"
              disabled={loading || cafeOrders.length === 0}
              onClick={() => {
                const fname = period === "custom"
                  ? `woxa-stats_${channel}_${moment(start).format("YYYYMMDD")}-${moment(end).format("YYYYMMDD")}.csv`
                  : `woxa-stats_${channel}_${period}_${moment().format("YYYY-MM-DD-HHmm")}.csv`;
                exportCsv(cafeOrders, fname);
              }}
              className="inline-flex items-center gap-2 h-[40px] px-5 rounded-full text-sm font-bold text-white whitespace-nowrap shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97] transition-all"
              style={{ background: C.espresso }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Channel toggle (Drinks / Desserts) ---------- */}
      <div className="px-5 pb-3">
        <div className="flex gap-2 p-1 rounded-2xl bg-white" style={{ boxShadow: "0 0 0 1px #E7DFD2" }}>
          {([
            { id: "cafe",    label: "Drinks",   emoji: "☕", count: channelCounts.cafe },
            { id: "dessert", label: "Desserts", emoji: "🍰", count: channelCounts.dessert },
          ] as const).map((c) => {
            const active = channel === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setChannel(c.id)}
                className="flex-1 inline-flex items-center justify-center gap-2 h-[40px] rounded-xl text-sm font-bold transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "transparent", color: C.espresso }
                }
              >
                <span aria-hidden>{c.emoji}</span>
                <span>{c.label}</span>
                <span
                  className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10px] font-black tabular-nums"
                  style={
                    active
                      ? { background: "rgba(255,251,235,0.25)", color: "#FFFBEB" }
                      : { background: "#F3EBDA", color: C.espresso }
                  }
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- Period chips ---------- */}
      <div className="px-5 pb-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {PERIODS.map((p) => {
            const active = p.id === period;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className="inline-flex items-center h-[36px] px-4 rounded-full text-sm font-bold whitespace-nowrap transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "white", color: C.espresso, boxShadow: "0 0 0 1px #E7DFD2" }
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {/* Date inputs — always visible. Editing switches to "custom". */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <label className="flex items-center gap-2 text-xs font-bold" style={{ color: C.espresso }}>
            <span className="opacity-70">From</span>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }}
              className="h-9 px-3 rounded-xl bg-white border-2 text-sm focus:outline-none"
              style={{ borderColor: "#E7DFD2", color: "#1B0F08" }}
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-bold" style={{ color: C.espresso }}>
            <span className="opacity-70">To</span>
            <input
              type="date"
              value={to}
              min={from}
              max={moment().format("YYYY-MM-DD")}
              onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }}
              className="h-9 px-3 rounded-xl bg-white border-2 text-sm focus:outline-none"
              style={{ borderColor: "#E7DFD2", color: "#1B0F08" }}
            />
          </label>
          {period === "custom" && (() => {
            const days = Math.max(1, moment(to).diff(moment(from), "days") + 1);
            return (
              <span
                className="inline-flex items-center px-2.5 h-7 rounded-full text-[11px] font-bold"
                style={{ background: "#FEF3C7", color: C.espresso }}
              >
                {days} {days === 1 ? "day" : "days"}
              </span>
            );
          })()}
        </div>
      </div>

      <main className="px-5 space-y-6">
        {/* ---------- Performance row ---------- */}
        <section>
          <Eyebrow icon="⚡" color={C.amber}>Performance</Eyebrow>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            <StatCard tint={C.amber}   icon="⏱" label="Avg brew time"      value={fmtDuration(k.avgBrewMs)} sub="brewing → ready" mono />
            <StatCard tint={C.sky}     icon="🕒" label="Avg wait at bar"    value={fmtDuration(k.avgWaitMs)} sub="ready → picked"  mono />
            <StatCard tint={C.violet}  icon="👥" label="Unique customers"   value={uniqueCustomers}          sub={`${avgPerCustomer} avg/person`} />
            <StatCard tint={C.rose}    icon="✕"  label="Cancellation"        value={`${cancelBreakdown.byUser} / ${cancelBreakdown.byBarista}`} sub="user / barista" mono />
          </div>
        </section>

        {/* ---------- Trend chart ---------- */}
        <section>
          <Eyebrow icon="📈" color={C.caramel}>
            {bucketBy === "hour" ? "Hour by hour" : bucketBy === "day" ? "Day by day" : "Month by month"}
          </Eyebrow>
          <div className="bg-white rounded-2xl p-4 mt-2" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
            {loading ? (
              <Spinner />
            ) : (
              <div className="flex items-end gap-1.5">
                {trend.map((b) => {
                  const isPeak = b.key === trendMaxKey;
                  return (
                    <div key={b.key} className="flex-1 min-w-0 flex flex-col items-center">
                      <div
                        className="text-[10px] font-extrabold tabular-nums h-[14px] leading-[14px]"
                        style={{ color: isPeak ? C.caramel : "#A38B6A" }}
                      >
                        {b.count || ""}
                      </div>
                      {/* Track with explicit height so bar's `height: X%` resolves correctly. */}
                      <div className="w-full h-[150px] flex items-end">
                        <div
                          className="w-full rounded-t-lg transition-all duration-500"
                          style={{
                            height: `${(b.count / trendMax) * 100}%`,
                            minHeight: b.count ? "6px" : "0",
                            background: isPeak
                              ? "linear-gradient(180deg,#F59E0B 0%,#D97706 100%)"
                              : "linear-gradient(180deg,#FDE68A 0%,#FBBF24 100%)",
                          }}
                        />
                      </div>
                      <div className="text-[10px] font-semibold mt-1" style={{ color: "#A38B6A" }}>
                        {bucketBy === "hour" ? b.key
                          : bucketBy === "day" ? moment(b.key).format("dd")[0]
                          : moment(b.key, "YYYY-MM").format("MMM")}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ---------- Day of week ---------- */}
        <section>
          <Eyebrow icon="📅" color={C.violet}>Busiest day</Eyebrow>
          <div className="bg-white rounded-2xl p-4 mt-2" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
            <div className="flex items-end gap-2">
              {dow.map((d, idx) => {
                const isMax = idx === dowMaxIdx && d.count > 0;
                return (
                  <div key={d.key} className="flex-1 flex flex-col items-center">
                    <div className="text-[10px] font-extrabold tabular-nums h-[14px] leading-[14px]" style={{ color: isMax ? C.violet : "#A38B6A" }}>
                      {d.count || ""}
                    </div>
                    <div className="w-full h-[100px] flex items-end">
                      <div
                        className="w-full rounded-t-md"
                        style={{
                          height: `${(d.count / dowMax) * 100}%`,
                          minHeight: d.count ? "5px" : "0",
                          background: isMax
                            ? "linear-gradient(180deg,#A78BFA 0%,#7C3AED 100%)"
                            : "linear-gradient(180deg,#DDD6FE 0%,#C4B5FD 100%)",
                        }}
                      />
                    </div>
                    <div className="text-[10px] font-bold mt-1" style={{ color: isMax ? C.violet : "#A38B6A" }}>
                      {d.key}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ---------- Top menus ---------- */}
        <section>
          <Eyebrow icon="🏆" color={C.caramel}>Top menus</Eyebrow>
          <div className="bg-white rounded-2xl mt-2 overflow-hidden" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
            {topMenus.length === 0 ? (
              <p className="px-4 py-5 text-sm text-center" style={{ color: "#A38B6A" }}>No data</p>
            ) : (
              topMenus.map((m, i) => (
                <div key={m.name} className="px-4 py-3 flex items-center gap-3" style={{ borderTop: i === 0 ? "0" : "1px solid #F3EBDA" }}>
                  <RankBadge rank={i + 1} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-black text-[15px] truncate" style={{ color: "#1B0F08" }}>{m.name}</p>
                    <div className="mt-1.5 h-[8px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(m.count / topMenuMax) * 100}%`,
                          background: i === 0
                            ? "linear-gradient(90deg,#F59E0B 0%,#D97706 100%)"
                            : i === 1
                            ? "linear-gradient(90deg,#A78BFA 0%,#7C3AED 100%)"
                            : i === 2
                            ? "linear-gradient(90deg,#FB923C 0%,#EA580C 100%)"
                            : `linear-gradient(90deg,${CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} 0%,${CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]}CC 100%)`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-base font-black tabular-nums" style={{ color: C.espresso }}>{m.count}</span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* ---------- By type (drinks-only) ---------- */}
        {!isDessert && (
        <section>
          <Eyebrow icon="🍵" color={C.emerald}>By type</Eyebrow>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {types.map((t) => (
              <div
                key={t.id}
                className="rounded-2xl p-4 text-white relative overflow-hidden"
                style={{ background: t.grad, boxShadow: "0 8px 24px -12px rgba(0,0,0,0.25)" }}
              >
                <div className="text-3xl mb-2">{t.emoji}</div>
                <div className="font-display font-black tabular-nums leading-none" style={{ fontSize: "28px" }}>
                  {t.count}
                </div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.12em] mt-1 opacity-90">{t.label}</div>
                {/* Sparkline-like proportion bar */}
                <div className="mt-3 h-[4px] rounded-full bg-white/30 overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${(t.count / typeMax) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
        )}

        {/* ---------- By category ---------- */}
        <section>
          <Eyebrow icon="🗂" color={C.slate}>By category</Eyebrow>
          <div className="bg-white rounded-2xl p-4 space-y-3 mt-2" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
            {categories.length === 0 ? (
              <p className="text-sm text-center py-3" style={{ color: "#A38B6A" }}>No data</p>
            ) : (
              categories.map((c, i) => {
                const color = CATEGORY_PALETTE[i % CATEGORY_PALETTE.length];
                const pct = Math.round((c.count / k.total) * 100);
                return (
                  <div key={c.name}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="flex items-center gap-2 font-bold" style={{ color: "#1B0F08" }}>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                        {c.name}
                      </span>
                      <span className="font-bold tabular-nums" style={{ color }}>
                        {c.count} <span className="font-semibold opacity-60">({pct}%)</span>
                      </span>
                    </div>
                    <div className="h-[10px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(c.count / categoryMax) * 100}%`,
                          background: `linear-gradient(90deg,${color} 0%,${color}CC 100%)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ---------- Sweetness (drinks-only) ---------- */}
        {!isDessert && (
        <section>
          <Eyebrow icon="🍯" color={C.pink}>Sweetness preference</Eyebrow>
          <div className="bg-white rounded-2xl p-4 mt-2" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
            <div className="grid grid-cols-5 gap-2">
              {sweetness.map((s) => {
                // Color ramp: light→deep pink as sweetness rises
                const ramp = ["#FCE7F3", "#FBCFE8", "#F9A8D4", "#F472B6", "#EC4899"];
                const idx = Math.min(4, Math.round(s.pct / 25));
                return (
                  <div key={s.key} className="text-center">
                    <div className="h-[90px] flex items-end justify-center mb-2">
                      <div
                        className="w-full rounded-t-md"
                        style={{
                          height: `${(s.count / sweetMax) * 100}%`,
                          minHeight: s.count ? "6px" : "0",
                          background: ramp[idx],
                        }}
                      />
                    </div>
                    <div className="text-xs font-black" style={{ color: ramp[idx] === "#FCE7F3" ? "#9D174D" : ramp[idx] }}>
                      {s.key}
                    </div>
                    <div className="text-[10px] font-semibold tabular-nums" style={{ color: "#A38B6A" }}>
                      {s.count}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {/* ---------- Cancellation analytics ---------- */}
        <section>
          <Eyebrow icon="🚫" color={C.rose}>
            Cancellation insights · {cancelledOrders.length} total
          </Eyebrow>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {/* Reason split */}
            <div className="bg-white rounded-2xl p-4" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.10em] mb-3" style={{ color: C.rose }}>
                Cancelled by
              </p>
              {cancelledOrders.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: "#A38B6A" }}>No cancellations</p>
              ) : (
                <div className="space-y-3">
                  {cancelReasons.map((r) => (
                    <div key={r.id}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-bold flex items-center gap-2" style={{ color: "#1B0F08" }}>
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                          {r.label}
                        </span>
                        <span className="font-black tabular-nums" style={{ color: r.color }}>
                          {r.count} <span className="font-semibold opacity-60">({r.pct}%)</span>
                        </span>
                      </div>
                      <div className="h-[10px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${r.pct}%`, background: r.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top cancelled menus */}
            <div className="bg-white rounded-2xl p-4" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.10em] mb-3" style={{ color: C.rose }}>
                Most cancelled items
              </p>
              {cancelledTopMenus.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: "#A38B6A" }}>No cancellations</p>
              ) : (
                <div className="space-y-2.5">
                  {cancelledTopMenus.map((m) => (
                    <div key={m.name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-bold truncate min-w-0 pr-2" style={{ color: "#1B0F08" }}>{m.name}</span>
                        <span className="font-black tabular-nums" style={{ color: C.rose }}>{m.count}</span>
                      </div>
                      <div className="h-[8px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(m.count / cancelledTopMenuMax) * 100}%`,
                            background: "linear-gradient(90deg,#FB7185 0%,#E11D48 100%)",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Reject notes (only when there are notes) */}
          {rejectNotes.length > 0 && (
            <div className="bg-white rounded-2xl p-4 mt-3" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.10em] mb-3" style={{ color: C.rose }}>
                Latest reject reasons
              </p>
              <div className="divide-y" style={{ borderColor: "#F3EBDA" }}>
                {rejectNotes.map((n) => (
                  <div key={n.id} className="py-2.5 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="font-black" style={{ color: "#1B0F08" }}>{n.menu}</span>
                      <span style={{ color: "#A38B6A" }}>·</span>
                      <span className="font-semibold" style={{ color: "#A38B6A" }}>{moment(n.when).fromNow()}</span>
                      <span style={{ color: "#A38B6A" }}>·</span>
                      <span className="font-semibold" style={{ color: C.rose }}>by {n.by}</span>
                    </div>
                    <p className="mt-1 text-sm font-semibold" style={{ color: "#5B3A29" }}>"{n.note}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ---------- Top customers ---------- */}
        <section>
          <Eyebrow icon="⭐" color={C.violet}>Top customers</Eyebrow>
          <div className="bg-white rounded-2xl mt-2 overflow-hidden" style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}>
            {topCustomers.length === 0 ? (
              <p className="px-4 py-5 text-sm text-center" style={{ color: "#A38B6A" }}>No data</p>
            ) : (
              topCustomers.map((c, i) => {
                const initial = c.name.trim().slice(0, 1).toUpperCase() || "?";
                const bg = colorFromName(c.name);
                return (
                  <div key={c.id} className="px-4 py-3 flex items-center gap-3" style={{ borderTop: i === 0 ? "0" : "1px solid #F3EBDA" }}>
                    <RankBadge rank={i + 1} />
                    <div
                      className="w-[40px] h-[40px] rounded-full text-white flex items-center justify-center font-display font-black flex-shrink-0 shadow-sm"
                      style={{ background: bg }}
                    >
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-black text-[15px] truncate" style={{ color: "#1B0F08" }}>{c.name}</p>
                      <div className="mt-1.5 h-[8px] rounded-full overflow-hidden" style={{ background: "#F3EBDA" }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${(c.count / topCustomerMax) * 100}%`, background: `linear-gradient(90deg,${bg} 0%,${bg} 100%)` }}
                        />
                      </div>
                    </div>
                    <span className="text-base font-black tabular-nums" style={{ color: C.espresso }}>{c.count}</span>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

// ----------------------------- subcomponents -----------------------------

const Spinner: React.FC = () => (
  <div className="h-[160px] flex items-center justify-center">
    <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: "#D97706" }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  </div>
);

const Eyebrow: React.FC<{ icon: string; color: string; children: React.ReactNode }> = ({ icon, color, children }) => (
  <p className="text-[11px] font-extrabold tracking-[0.10em] uppercase flex items-center gap-1.5" style={{ color }}>
    <span className="text-[14px] leading-none">{icon}</span>
    {children}
  </p>
);

const MiniStat: React.FC<{ tint: string; value: number | string; label: string; mono?: boolean }> = ({ tint, value, label, mono }) => (
  <div className="px-3 py-2 rounded-xl bg-white/60 backdrop-blur" style={{ boxShadow: "0 0 0 1px rgba(91,58,41,0.08)" }}>
    <div className={`font-display font-black leading-none ${mono ? "tabular-nums" : ""}`} style={{ color: tint, fontSize: "22px" }}>
      {value}
    </div>
    <div className="text-[10px] font-bold uppercase tracking-wider mt-0.5" style={{ color: "#5B3A29" }}>{label}</div>
  </div>
);

const StatCard: React.FC<{
  tint: string; icon: string; label: string; value: number | string; sub?: string; mono?: boolean;
}> = ({ tint, icon, label, value, sub, mono }) => (
  <div
    className="rounded-2xl bg-white p-4 relative overflow-hidden"
    style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 16px -12px rgba(91,58,41,0.18)" }}
  >
    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ background: tint }} />
    <div className="flex items-start justify-between">
      <p className="text-[10px] font-extrabold uppercase tracking-[0.10em]" style={{ color: tint }}>{label}</p>
      <span className="text-base leading-none">{icon}</span>
    </div>
    <p className={`font-display font-black mt-2 leading-[1] ${mono ? "tabular-nums" : ""}`} style={{ color: "#1B0F08", fontSize: "26px" }}>
      {value}
    </p>
    {sub && <p className="text-[11px] font-semibold mt-1" style={{ color: "#A38B6A" }}>{sub}</p>}
  </div>
);

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const palette =
    rank === 1 ? { bg: "linear-gradient(135deg,#FCD34D 0%,#D97706 100%)", color: "#1B0F08" }
    : rank === 2 ? { bg: "linear-gradient(135deg,#E5E7EB 0%,#9CA3AF 100%)", color: "#1B0F08" }
    : rank === 3 ? { bg: "linear-gradient(135deg,#FCA5A5 0%,#B45309 100%)", color: "#FFFFFF" }
    : { bg: "#F3EBDA", color: "#A38B6A" };
  return (
    <span
      className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-xs font-black tabular-nums flex-shrink-0"
      style={{ background: palette.bg, color: palette.color }}
    >
      {rank}
    </span>
  );
};

const Ring: React.FC<{ percent: number; color: string; label: string }> = ({ percent, color, label }) => {
  const size = 84;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (Math.max(0, Math.min(100, percent)) / 100);
  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#FFFFFF" strokeOpacity="0.7" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
      </svg>
      <div>
        <div className="font-display font-black leading-none tabular-nums" style={{ color: "#1B0F08", fontSize: "28px" }}>
          {percent}%
        </div>
        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#5B3A29" }}>{label}</div>
      </div>
    </div>
  );
};

export default StatsPage;
